import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { heroComics } from '@/mocks/comics';

export default function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goToSlide = useCallback(
    (index: number) => {
      if (isTransitioning) return;
      setIsTransitioning(true);
      setCurrentSlide(index);
      setTimeout(() => setIsTransitioning(false), 600);
    },
    [isTransitioning]
  );

  useEffect(() => {
    const timer = setInterval(() => {
      goToSlide((currentSlide + 1) % heroComics.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [currentSlide, goToSlide]);

  const currentComic = heroComics[currentSlide];

  return (
    <section className="relative w-full h-[520px] md:h-[650px] overflow-hidden">
      {heroComics.map((comic, idx) => (
        <div
          key={comic.id}
          className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
            idx === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={comic.imageUrl}
            alt={comic.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/30"></div>
        </div>
      ))}

      <div className="relative z-10 h-full flex items-center">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
              <div className="lg:w-2/5 text-center lg:text-left animate-fade-in-up">
                <span className="inline-block bg-accent-500 text-background-50 text-xs font-semibold px-3 py-1 rounded-full mb-4 whitespace-nowrap">
                  🔥 热门推荐
                </span>
                <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 leading-tight mb-4">
                  {currentComic.title}
                </h1>
                <p className="text-base md:text-lg text-background-50/80 leading-relaxed mb-6 max-w-lg mx-auto lg:mx-0">
                  {currentComic.description}
                </p>
                <div className="flex flex-wrap items-center gap-3 justify-center lg:justify-start mb-6">
                  {currentComic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs text-background-50/90 bg-background-50/15 px-3 py-1 rounded-full whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-5 justify-center lg:justify-start text-sm text-background-50/70 mb-6">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i> {currentComic.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500"></i> {currentComic.rating}
                  </span>
                  <span>
                    共{currentComic.chapters}话
                  </span>
                </div>
                <Link
                  to={`/article/${currentComic.slug}`}
                  className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-7 py-3 rounded-full text-base font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  立即阅读
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>

              <div className="hidden lg:block lg:w-3/5">
                <div className="relative">
                  <div className="bg-background-50/10 backdrop-blur-sm rounded-2xl p-2">
                    <div className="flex items-center gap-2 mb-2 px-2">
                      <div className="flex gap-1.5">
                        <div className="w-3 h-3 rounded-full bg-red-400"></div>
                        <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                        <div className="w-3 h-3 rounded-full bg-green-400"></div>
                      </div>
                      <span className="text-xs text-background-50/60 ml-2">hanmanzhijia.com</span>
                    </div>
                    <div className="relative rounded-xl overflow-hidden">
                      <img
                        src={currentComic.imageUrl}
                        alt={currentComic.title}
                        className="w-full h-[380px] object-cover object-top"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-primary-950/90 to-transparent p-5">
                        <div className="flex items-center gap-2 text-xs text-background-50/80">
                          <span className="bg-accent-500 text-background-50 px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap">
                            {currentComic.category}
                          </span>
                          <span>{currentComic.views} 次阅读</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {heroComics.map((_, idx) => (
          <button
            key={idx}
            onClick={() => goToSlide(idx)}
            className={`w-2.5 h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
              idx === currentSlide
                ? 'bg-accent-500 w-8'
                : 'bg-background-50/40 hover:bg-background-50/60'
            }`}
            aria-label={`幻灯片 ${idx + 1}`}
          />
        ))}
      </div>
    </section>
  );
}